import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../lib/moment';
import type { CancelChargeOriginalMappingResponseArgs } from './cancel_charge_original_mapping2';
import CancelChargeOriginalMapping, {
  convertCancelChargeOriginalMappingResponseToArgs
} from './cancel_charge_original_mapping2';

export type CancelChargeElementResponseArgs = {
  id: number;
  content: string;
  canceled_at?: string;
  start_at?: string;
  end_at?: string;
  cancel_charge_original_mappings?: CancelChargeOriginalMappingResponseArgs[];
};

export type CancelChargeElementArgs = {
  id: number;
  content: string;
  canceledAt?: Moment;
  startAt?: Moment;
  endAt?: Moment;
  mappings?: CancelChargeOriginalMapping[];
};

export const convertCancelChargeElementResponseToArgs = (response: CancelChargeElementResponseArgs) => {
  const args: CancelChargeElementArgs = {
    id: response.id,
    content: response.content,
    canceledAt: response.canceled_at ? moment(response.canceled_at) : undefined,
    startAt: response.start_at ? moment(response.start_at).startOf('day') : undefined,
    endAt: response.end_at ? moment(response.end_at).endOf('day') : undefined,
    mappings: response.cancel_charge_original_mappings
      ? response.cancel_charge_original_mappings.map(
          m => new CancelChargeOriginalMapping(convertCancelChargeOriginalMappingResponseToArgs(m))
        )
      : []
  };
  return args;
};

export default class CancelChargeElement {
  @observable id: number;
  @observable content: string;
  @observable canceledAt?: Moment;
  @observable startAt?: Moment;
  @observable endAt?: Moment;
  @observable mappings: CancelChargeOriginalMapping[];

  constructor(args: CancelChargeElementArgs) {
    this.id = args.id;
    this.content = args.content;
    this.canceledAt = args.canceledAt;
    this.startAt = args.startAt;
    this.endAt = args.endAt;
    this.mappings = args.mappings || [];
  }

  get type() {
    return 'cancel';
  }

  get title() {
    return 'キャンセル料';
  }

  get summary() {
    return this.content;
  }

  get name() {
    return this.title;
  }

  detail() {
    return '';
  }

  mappingsWithSettings(): CancelChargeOriginalMapping[] {
    return this.mappings.filter(m => m.originalTraceId);
  }

  addMapping(): void {
    this.mappings.push(new CancelChargeOriginalMapping({}));
  }

  removeMapping(index: number): void {
    this.mappings.splice(index, 1);
  }

  validationErrors(_orderItemStatus?: number): string[] {
    const errors: string[] = [];

    if (!this.content || this.content === '') {
      errors.push('キャンセル料の内容を入力してください。');
    }

    if (this.endAt && this.startAt && this.endAt < this.startAt) {
      errors.push('利用開始以降を指定してください。');
    }

    if (this.mappingsWithSettings().length < 1) {
      errors.push('キャンセル対象を選択してください。');
    }

    return errors;
  }

  get description(): string {
    let desc = `<キャンセル>
■内容
${this.content}
■キャンセル日`;
    if (this.canceledAt) {
      desc += `\n${this.canceledAt.format('YYYY年MM月DD日')}`;
    }
    return desc;
  }

  get updateParams() {
    return {
      type: this.type,
      content: this.content,
      canceled_at: this.canceledAt?.format('YYYY-MM-DD'),
      start_at: this.startAt?.format('YYYY-MM-DD HH:mm'),
      end_at: this.endAt?.format('YYYY-MM-DD HH:mm'),
      cancel_charge_original_mappings_attributes: this.mappingsWithSettings().map(m => m.updateParams)
    };
  }

  get startTime() {
    return this.startAt;
  }

  get endTime() {
    return this.endAt;
  }
}
